import { useEffect, useState } from "react";
import Layout from "@/components/eoffice/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Mail, UserPlus, Copy, X, CheckCircle2, Trash2, AlertTriangle, Pencil, KeyRound, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { usePaged, Pager } from "@/components/eoffice/Pager";

function fmt(dt) {
  try { return new Date(dt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }); }
  catch { return dt; }
}

export default function Admin() {
  const { user: me } = useAuth();
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const invitesPaged = usePaged(invites, "invites");
  const usersPaged = usePaged(users, "users");
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("");
  const [role, setRole] = useState("user");
  const [busy, setBusy] = useState(false);
  const [lastInvite, setLastInvite] = useState(null); // { link, email_sent, email_error }

  // Edit user dialog state
  const [editUser, setEditUser] = useState(null); // full user obj being edited
  const [editName, setEditName] = useState("");
  const [editDesignation, setEditDesignation] = useState("");
  const [editRole, setEditRole] = useState("user");
  const [editNewPw, setEditNewPw] = useState("");
  const [editShowPw, setEditShowPw] = useState(false);
  const [editBusy, setEditBusy] = useState(false);

  const load = async () => {
    try {
      const [i, u] = await Promise.all([api.get("/invites"), api.get("/users")]);
      setInvites(i.data); setUsers(u.data);
    } catch { toast.error("Failed to load admin data"); }
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!email || !name || !designation) { toast.error("All fields required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/invites", { email, name, designation, role });
      setLastInvite({ email, link: data.invite_link, email_sent: !!data.email_sent, email_error: data.email_error || "" });
      if (data.email_sent) {
        toast.success("Invitation email sent");
      } else {
        toast.warning("Invitation created — email delivery failed. Share the link manually.");
      }
      try { await navigator.clipboard.writeText(data.invite_link); }
      catch (clipErr) { console.warn("Clipboard write failed:", clipErr); }
      setOpen(false); setEmail(""); setName(""); setDesignation(""); setRole("user");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revokeInvite = async (id) => {
    try { await api.delete(`/invites/${id}`); toast.success("Invite revoked"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Deactivate user "${u.name}" (${u.email})? They will no longer be able to sign in. Their past contributions to files remain intact.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deactivated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditName(u.name || "");
    setEditDesignation(u.designation || "");
    setEditRole(u.role || "user");
    setEditNewPw("");
    setEditShowPw(false);
  };
  const closeEdit = () => setEditUser(null);
  const saveEdit = async () => {
    if (!editUser) return;
    setEditBusy(true);
    try {
      const patch = {};
      if (editName.trim() !== editUser.name) patch.name = editName.trim();
      if (editDesignation.trim() !== editUser.designation) patch.designation = editDesignation.trim();
      if (editRole !== editUser.role) patch.role = editRole;
      if (Object.keys(patch).length > 0) {
        await api.patch(`/users/${editUser.id}`, patch);
      }
      if (editNewPw) {
        if (editNewPw.length < 6) { toast.error("New password must be at least 6 characters"); setEditBusy(false); return; }
        await api.post(`/users/${editUser.id}/reset-password`, { new_password: editNewPw });
      }
      toast.success("User updated");
      closeEdit();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setEditBusy(false); }
  };

  const copy = (link) => {
    navigator.clipboard.writeText(link).then(() => toast.success("Link copied"));
  };

  return (
    <Layout>
      <div className="px-4 md:px-10 py-6 md:py-10">
        <div className="flex flex-wrap gap-4 items-end justify-between mb-8 border-b border-[#DCD9D0] pb-6">
          <div>
            <div className="font-mono-eo text-[10px] uppercase tracking-[0.3em] text-[#8A8A80]">// Administration</div>
            <h1 className="font-heading text-4xl md:text-[44px] font-bold leading-none mt-2">Users & Invitations</h1>
            <p className="text-[14px] text-[#5C5C54] mt-3">Invite colleagues via email and manage active portal members.</p>
          </div>
          <button data-testid={ADMIN.inviteBtn} onClick={() => setOpen(true)} className="eo-btn-primary flex items-center gap-2"><UserPlus size={16} /> Invite user</button>
        </div>

        {/* Last invite banner — shows link so admin can share manually if email fails */}
        {lastInvite && (
          <div
            data-testid="last-invite-banner"
            className={`border p-4 mb-6 flex items-start gap-3 ${lastInvite.email_sent ? "border-[#2A9D8F] bg-[#E6F4F1]" : "border-[#F4A261] bg-[#FCEFDE]"}`}
          >
            {lastInvite.email_sent
              ? <CheckCircle2 size={18} className="text-[#2A9D8F] mt-0.5 shrink-0" />
              : <AlertTriangle size={18} className="text-[#B8752E] mt-0.5 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="font-heading text-[15px] font-bold">
                {lastInvite.email_sent
                  ? `Invitation email sent to ${lastInvite.email}`
                  : `Invitation created — email delivery failed`}
              </div>
              {!lastInvite.email_sent && lastInvite.email_error && (
                <div className="text-[12px] text-[#7A4B10] font-mono-eo mt-1 break-all">Reason: {lastInvite.email_error}</div>
              )}
              <div className="text-[12px] text-[#5C5C54] mt-2">Shareable link (copied to clipboard):</div>
              <div className="font-mono-eo text-[12px] text-[#0A0A0A] break-all bg-white/70 border border-[#DCD9D0] p-2 mt-1">{lastInvite.link}</div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={() => copy(lastInvite.link)} className="eo-btn-ghost text-[12px] flex items-center gap-1"><Copy size={12} /> Copy</button>
              <button onClick={() => setLastInvite(null)} className="eo-btn-ghost text-[12px] flex items-center gap-1"><X size={12} /> Dismiss</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Invitations */}
          <div className="border border-[#DCD9D0] bg-white">
            <div className="px-6 py-3 border-b border-[#DCD9D0] bg-[#FBFAF3] font-mono-eo text-[11px] uppercase tracking-widest text-[#5C5C54]">// Invitations</div>
            {invites.length === 0 && <div className="p-8 text-center text-[13px] text-[#8A8A80]">No invitations yet.</div>}
            {invitesPaged.items.map((i) => {
              const link = `${window.location.origin}/accept-invite?token=${i.token}`;
              return (
                <div key={i.id} data-testid={ADMIN.inviteRow(i.id)} className="px-6 py-4 border-b border-[#DCD9D0]">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-[14px] truncate flex items-center gap-2">
                        <Mail size={14} className="text-[#1D3557]" /> {i.email}
                      </div>
                      <div className="font-mono-eo text-[11px] text-[#8A8A80] mt-1">{i.name} · {i.designation} · {i.role}</div>
                      <div className="font-mono-eo text-[10px] text-[#8A8A80] mt-1">Sent {fmt(i.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {i.used && !i.revoked ? (
                        <span className="text-[11px] font-mono-eo uppercase tracking-widest text-[#2A9D8F] border border-[#2A9D8F] bg-[#E6F4F1] px-2 py-1 flex items-center gap-1"><CheckCircle2 size={11} /> Accepted</span>
                      ) : i.revoked ? (
                        <span className="text-[11px] font-mono-eo uppercase tracking-widest text-[#8A8A80] border border-[#DCD9D0] px-2 py-1">Revoked</span>
                      ) : (
                        <>
                          <button onClick={() => copy(link)} className="eo-btn-ghost text-[12px] flex items-center gap-1"><Copy size={12} /> Copy link</button>
                          <button onClick={() => revokeInvite(i.id)} className="eo-btn-ghost text-[12px] flex items-center gap-1 border-[#E63946] text-[#E63946]"><X size={12} /> Revoke</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <Pager {...invitesPaged} testid="invites-pager" />
          </div>

          {/* Users */}
          <div className="border border-[#DCD9D0] bg-white">
            <div className="px-6 py-3 border-b border-[#DCD9D0] bg-[#FBFAF3] font-mono-eo text-[11px] uppercase tracking-widest text-[#5C5C54]">// Active Users</div>
            {usersPaged.items.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="px-6 py-3 border-b border-[#DCD9D0] flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#1D3557] text-white flex items-center justify-center font-heading font-bold text-[13px]">{u.name?.[0]?.toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[14px] truncate">{u.name}{isSelf && <span className="text-[#8A8A80] font-normal text-[12px]"> (you)</span>}</div>
                    <div className="font-mono-eo text-[11px] text-[#8A8A80] truncate">{u.email} · {u.designation}</div>
                  </div>
                  <span className="text-[10px] font-mono-eo uppercase tracking-widest border px-2 py-1" style={{ borderColor: u.role === "admin" ? "#1D3557" : "#DCD9D0", color: u.role === "admin" ? "#1D3557" : "#5C5C54" }}>{u.role}</span>
                  {!isSelf && (
                    <>
                      <button
                        data-testid={`user-edit-${u.id}`}
                        onClick={() => openEdit(u)}
                        className="eo-btn-ghost text-[12px] flex items-center gap-1"
                        title="Edit user (role / password)"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        data-testid={`user-delete-${u.id}`}
                        onClick={() => deleteUser(u)}
                        className="eo-btn-ghost text-[12px] flex items-center gap-1 border-[#E63946] text-[#E63946] hover:bg-[#FBECEE]"
                        title="Deactivate user"
                      >
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
            <Pager {...usersPaged} testid="users-pager" />
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Invite user</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Email</label>
              <input data-testid={ADMIN.inviteEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="eo-input" />
            </div>
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Full name</label>
              <input data-testid={ADMIN.inviteName} value={name} onChange={(e) => setName(e.target.value)} className="eo-input" />
            </div>
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Designation</label>
              <input data-testid={ADMIN.inviteDesignation} value={designation} onChange={(e) => setDesignation(e.target.value)} className="eo-input" placeholder="e.g. Section Officer" />
            </div>
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Role</label>
              <select data-testid={ADMIN.inviteRole} value={role} onChange={(e) => setRole(e.target.value)} className="eo-input">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <p className="text-[11px] font-mono-eo text-[#8A8A80]">An email with an invitation link will be sent. The link is also copied to your clipboard so you can share it manually if needed.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="eo-btn-ghost">Cancel</button>
            <button data-testid={ADMIN.inviteSubmit} disabled={busy} onClick={send} className="eo-btn-primary">{busy ? "Sending..." : "Send invitation"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User dialog */}
      <Dialog open={!!editUser} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent className="border-[#DCD9D0] rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-[22px]">Edit user</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 py-2">
              <div className="border border-[#DCD9D0] bg-[#FBFAF3] p-3 text-[12px] font-mono-eo text-[#5C5C54]">
                {editUser.email}
              </div>
              <div>
                <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Full name</label>
                <input data-testid="edit-user-name" value={editName} onChange={(e) => setEditName(e.target.value)} className="eo-input" />
              </div>
              <div>
                <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Designation</label>
                <input data-testid="edit-user-designation" value={editDesignation} onChange={(e) => setEditDesignation(e.target.value)} className="eo-input" />
              </div>
              <div>
                <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Role</label>
                <select data-testid="edit-user-role" value={editRole} onChange={(e) => setEditRole(e.target.value)} className="eo-input">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5 flex items-center gap-2">
                  <KeyRound size={12} /> New password (optional)
                </label>
                <div className="relative">
                  <input
                    data-testid="edit-user-new-password"
                    type={editShowPw ? "text" : "password"}
                    value={editNewPw}
                    onChange={(e) => setEditNewPw(e.target.value)}
                    className="eo-input pr-10"
                    placeholder="Leave blank to keep current password"
                  />
                  <button
                    type="button"
                    data-testid="edit-user-password-toggle"
                    onClick={() => setEditShowPw((v) => !v)}
                    aria-label={editShowPw ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5C5C54] hover:text-[#0A0A0A]"
                    tabIndex={-1}
                  >
                    {editShowPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-[11px] font-mono-eo text-[#8A8A80] mt-1.5">Minimum 6 characters. The user is not notified — please share the new password with them securely.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={closeEdit} className="eo-btn-ghost">Cancel</button>
            <button data-testid="edit-user-save" disabled={editBusy} onClick={saveEdit} className="eo-btn-primary">{editBusy ? "Saving..." : "Save changes"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
